interface Choice {
  value: string;
  display_name: string;
}

interface DefaultValue {
  refurbishment_sector: string;
  construction_year_class: string;
  refurbishment_value: number;
}

interface DefaultValue {
  max_value: number;
  min_value: number;
  required: boolean;
}

interface Options {
  refurbishment_sector: {
    choices: Choice[];
  };
  construction_year_class: {
    choices: Choice[];
  };
  default_values: DefaultValue[];
  default_values_percent: DefaultValue[];
  refurbishment_unit: {
    choices: Choice[];
  };
  refurbishment_value: {
    max_value: number;
    min_value: number;
    required: boolean;
  };
}

interface Field {
  key: string;
  label: string;
  value: number;
}

interface Sector {
  key: string;
  label: string;
  fields: Field[];
}

interface Payload {
  refurbishment_sector: string;
  construction_year_class: number;
  refurbishment_value: number;
  refurbishment_unit: number;
}

export default class Refurbishment {
  options: Options;
  defaultsPercent: Record<string, number>;
  defaultsKWh: Record<string, number>;
  sectors: Sector[];
  unit: number;
  unitOptions: Choice[];
  validationRules;

  constructor(options: Options) {
    this.options = options;
    this.defaultsKWh = this.getOptimizedDefaults('default_values');
    this.defaultsPercent = this.getOptimizedDefaults('default_values_percent');
    this.unit = 0;
    this.sectors = this.getSectors();
    this.unitOptions = options.refurbishment_unit.choices;
    this.validationRules = this.getValidationRules();
  }

  getSectors(): Sector[] {
    return this.options.refurbishment_sector.choices.map((sector: Choice) => {
      return {
        key: sector.value,
        label: sector.display_name,
        fields: this.getFields(sector),
      };
    });
  }

  getFields(sector: Choice): Field[] {
    const isUnitKWh = this.unit === 0;
    const defaults = isUnitKWh ? this.defaultsKWh : this.defaultsPercent;

    return this.options.construction_year_class.choices.map(
      (yearClass: Choice) => {
        const key = `${sector.value}${yearClass.value}`;
        return {
          key,
          label: yearClass.display_name,
          value: defaults[key],
        };
      },
    );
  }

  getOptimizedDefaults(
    key: 'default_values' | 'default_values_percent',
  ): Record<string, number> {
    const defaults = this.options[key];
    return defaults.reduce((acc: Record<string, number>, cv) => {
      acc[`${cv.refurbishment_sector}${cv.construction_year_class}`] =
        cv.refurbishment_value;
      return acc;
    }, {});
  }

  initFromRequest(fields: Payload[]): void {
    const fieldMap: Record<string, Field> = this.getAllFields().reduce(
      (acc, cv) => {
        acc[cv.key] = cv;
        return acc;
      },
      {} as Record<string, Field>,
    );

    this.unit = fields[0].refurbishment_unit;
    fields.forEach((field) => {
      const key = `${field.refurbishment_sector}${field.construction_year_class}`;
      fieldMap[key].value = field.refurbishment_value;
    });
  }

  get payload(): Payload[] {
    return this.getAllFields().map((field) => {
      const sectorValue = field.key.slice(0, -1);
      const yearClassValue = Number(field.key.slice(-1));
      return {
        refurbishment_sector: sectorValue,
        construction_year_class: yearClassValue,
        refurbishment_value: field.value,
        refurbishment_unit: this.unit,
      };
    });
  }

  getAllFields(sectors = this.sectors): Field[] {
    return sectors.flatMap((sector) => sector.fields);
  }

  isValid(sectors = this.sectors) {
    console.log('this.allFields', this.getAllFields(sectors));
    return this.getAllFields(sectors).every(
      (field) =>
        field.value !== null &&
        field.value <= this.validationRules.max &&
        field.value >= this.validationRules.min,
    );
  }

  getValidationRules() {
    return {
      max: this.options.refurbishment_value.max_value,
      min: this.options.refurbishment_value.min_value,
      required: this.options.refurbishment_value.required,
    };
  }
}
